import React from 'react';
import { RootState } from 'core/store';
import { menuController } from '@ionic/core/components';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { hasWarrantySubscription, isDev, openHelp } from 'core/util';
import {
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonMenu,
  IonSpinner
} from '@ionic/react';
import Illustration from 'components/Illustrations/Illustration';
import HomeIcon from 'assets/icons/home.svg';
import InventoryIcon from 'assets/icons/inventory.svg';
import ContactsIcon from 'assets/icons/contacts.svg';
import DocumentsIcon from 'assets/icons/documents.svg';
// import ConstructionIcon from 'assets/icons/construction.svg';
import AvidWarrantyIcon from 'assets/icons/aw-warranty.svg';
import './SideMenu.scss';
import HomeSwitcher from 'components/home-switcher/HomeSwitcher';
import { useCustomTokenLink } from 'core/hooks/useCustomTokenLink';

interface Page {
  title: string;
  path: string;
  icon: string;
  hidden: boolean;
}

const SideMenu: React.FC = ({ children }) => {
  const { platformType, version } = useSelector(
    (state: RootState) => state.platform
  );
  const {
    currentHome,
    homes,
    builderLogo,
    warrantyURL,
    globalAccountSettings
  } = useSelector((state: RootState) => state.home);
  const { accessToken } = useSelector((state: RootState) => state.login);

  const location = useLocation();
  const history = useHistory();
  const { createCustomTokenLink } = useCustomTokenLink();

  const pages: Page[] = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: String(HomeIcon),
      hidden: false
    },
    {
      title: 'Contacts',
      path: '/contacts',
      icon: String(ContactsIcon),
      hidden: false
    },
    {
      title: 'Inventory',
      path: '/inventory',
      icon: String(InventoryIcon),
      hidden: false
    },
    {
      title: 'Documents',
      path: '/documents',
      icon: String(DocumentsIcon),
      hidden: false
    },
    // {
    //   title: 'Construction',
    //   path: '/home-construction',
    //   icon: String(ConstructionIcon),
    //   hidden: false
    // },
    {
      ...(warrantyURL &&
      currentHome &&
      hasWarrantySubscription(currentHome?.subscriptions)
        ? ({
            title: 'Warranty',
            path: warrantyURL,
            icon: String(AvidWarrantyIcon),
            hidden: false
          } as Page)
        : ({} as Page))
    }
  ];

  const closeMenuAndShowHelp = () => {
    menuController.close();
    openHelp(platformType);
  };

  const navigateTo = (route: string) => {
    const isExternal = /^https?:\/\//.test(route);
    if (isExternal && accessToken) {
      createCustomTokenLink(
        accessToken,
        globalAccountSettings?.avid_warranty_url!
      );
    } else {
      history.push(route);
    }
    menuController.close();
  };

  return (
    <IonMenu
      side="start"
      type="push"
      contentId="main-content"
      className="main-menu"
    >
      <IonHeader scroll-y="false">
        {builderLogo ? (
          <Illustration image={builderLogo} />
        ) : (
          <IonSpinner name="crescent" />
        )}
      </IonHeader>
      <IonContent>
        {!!homes && homes.length > 1 && <HomeSwitcher />}
        <IonList
          className={`${!!homes && homes.length > 1 && 'main-menu-items'}`}
        >
          {pages.map((page, i) => {
            return (
              <IonItem
                key={`menu-item-${i}`}
                color={location.pathname.includes(page.path) ? 'grey' : 'dark'}
                onClick={() => navigateTo(page.path)}
                hidden={page.hidden}
              >
                <IonIcon icon={page.icon} slot="start" />
                {page.title}
              </IonItem>
            );
          })}
        </IonList>
      </IonContent>
      <div id="side-menu-footer">
        <IonList>
          <IonItem color="dark" onClick={closeMenuAndShowHelp}>
            Help
          </IonItem>
          <IonItem color="dark" />
          <IonItem color="dark" className="no-bold">
            {version} {isDev() && <div className="dev-tag">DEV</div>}
          </IonItem>
        </IonList>
      </div>
    </IonMenu>
  );
};

export default SideMenu;
